import { ArrowClockwise, Bank } from '@phosphor-icons/react'
import { errorMessage } from '../../api/core'
import { maskAccount } from '../../api/payouts'
import { useUrlState } from '../../lib/useUrlState'
import { Money } from '../../components/bits'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  Counter,
  Counters,
  Done,
  FactRow,
  Facts,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  Tag,
  TwoLine,
  type BoardColumn,
} from '../../components/board'
import { boardDate, boardWhen, hoursSinceIso, type FailureItem } from '../../model/payoutBoard'
import { FarmerCode, FarmerPhone } from './shared'
import { useOpenFailures } from './useOpenFailures'

const kindLabel = (kind: string) => (kind === 'TOP_UP' ? 'Top-up' : 'Lot payment')
const isCritical = (f: FailureItem) => f.severity === 'CRITICAL' || (hoursSinceIso(f.openedAt) ?? 0) >= 24
const bankWords = (f: FailureItem) => `${f.payout.status === 'REVERSED' ? 'Returned' : 'Failed'}: ${f.payout.failureReason ?? 'no reason from the bank'}`

/**
 * Payouts · failed and returned (alignment map V1-F4-B4a BankRejects; F4-B4.a): every open failure
 * with the bank's reason, whether it is about his account or worth trying again, the tries so far
 * and how long it has been open (critical after 24 h). The panel says what the system already did
 * and leads to Fix bank details (F4-B4.c) or Pay again (F4-B4.b).
 */
export function FailedPayoutsPage() {
  const [f, setF] = useUrlState({ open: '' })
  const failures = useOpenFailures()
  const items = failures.items
  const selected = items.find((x) => x.payout.id === f.open) ?? items[0] ?? null
  const columns: BoardColumn<FailureItem>[] = [
    {
      id: 'who',
      header: 'Farmer · lot · kind',
      cell: (x) => (
        <TwoLine
          top={x.payout.farmerName ?? '—'}
          sub={
            <>
              <FarmerCode id={x.payout.farmerId} /> · {x.payout.villageName ?? '—'}
            </>
          }
        >
          <span className="text-[14px]">{x.payout.unitCode}</span>
          <Tag tone={x.kind === 'TOP_UP' ? 'help' : 'plain'}>{kindLabel(x.kind)}</Tag>
        </TwoLine>
      ),
    },
    { id: 'amount', header: 'Amount', align: 'right', cell: (x) => <Money paise={x.payout.amountPaise} className="font-sans" /> },
    {
      id: 'account',
      header: 'Account',
      hideBelow: 'sm',
      cell: (x) => (
        <span className="flex flex-col leading-tight">
          <span>{maskAccount(x.payout.accountLast4).replace(' ', '')}</span>
          <span className="text-[13px] text-muted">{x.payout.accountIfsc ?? '—'}</span>
        </span>
      ),
    },
    {
      id: 'reason',
      header: 'Reason from bank',
      cell: (x) => (
        <span className="flex flex-col items-start gap-1">
          {bankWords(x)}
          {x.reasonClass === 'ACCOUNT' ? <Tag tone="bad">About the account</Tag> : <Tag tone="paper">Worth trying again</Tag>}
        </span>
      ),
    },
    { id: 'tries', header: 'Tries', hideBelow: 'md', cell: (x) => x.tries },
    {
      id: 'open',
      header: 'Open',
      cell: (x) => {
        const h = hoursSinceIso(x.openedAt)
        return (
          <span className="flex flex-col items-start gap-1">
            <b className={isCritical(x) ? 'text-danger' : undefined}>{h === null ? '—' : `${h} h`}</b>
            {isCritical(x) && <Tag tone="bad">Critical</Tag>}
          </span>
        )
      },
    },
  ]
  return (
    <BoardPage title="Payouts · failed and returned" actions={<LinkButton to="/c/payouts">All payouts</LinkButton>}>
      <BoardColumns
        main={
          <>
            <Counters>
              <Counter label="Open failures" value={items.length} loading={failures.isLoading} />
              <Counter label="Over 24 h" value={items.filter((x) => (hoursSinceIso(x.openedAt) ?? 0) >= 24).length} loading={failures.isLoading} />
              <Counter label="About the account" value={items.filter((x) => x.reasonClass === 'ACCOUNT').length} loading={failures.isLoading} />
              <Counter label="Worth trying again" value={items.filter((x) => x.reasonClass === 'RETRY').length} loading={failures.isLoading} />
            </Counters>
            <BoardTable<FailureItem>
              caption="Failed and returned payments"
              columns={columns}
              rows={items}
              getRowId={(x) => x.payout.id}
              selectedId={selected?.payout.id ?? null}
              onRowClick={(x) => setF({ open: x.payout.id })}
              loading={failures.isLoading}
              error={failures.error ? errorMessage(failures.error) : undefined}
              empty="No open failures. Every failed or returned payment has been paid again."
            />
            <Note>
              An unknown bank reason counts as an account problem until someone maps it. A repeated bank message is stored once; one that fails the signature
              check is refused and logged.
            </Note>
          </>
        }
        side={selected ? <FailurePanel x={selected} /> : undefined}
      />
    </BoardPage>
  )
}

function FailurePanel({ x }: { x: FailureItem }) {
  const p = x.payout
  const h = hoursSinceIso(x.openedAt)
  const account = x.reasonClass === 'ACCOUNT'
  return (
    <SidePanel
      label="Selected failure"
      title={`${p.unitCode} · ${p.farmerName ?? 'Farmer'}`}
      sub={
        <>
          <FarmerCode id={p.farmerId} /> · {p.villageName ?? '—'} · {kindLabel(p.kind).toLowerCase()}
          {p.collectionDay ? ` · closed ${boardDate(p.collectionDay)}` : ''}
        </>
      }
    >
      <Pill tone="bad">
        {p.status === 'REVERSED' ? 'Returned' : 'Failed'}
        {h !== null ? ` · open ${h} h` : ''}
        {isCritical(x) ? ' · critical' : ''}
      </Pill>
      <Facts>
        <FactRow label="Amount" strong>
          <Money paise={p.amountPaise} className="font-sans" />
        </FactRow>
        <FactRow label="Account">
          {maskAccount(p.accountLast4).replace(' ', '')} · {p.accountIfsc ?? '—'}
        </FactRow>
        <FactRow label="Reason from bank">{p.failureReason ?? 'not given'}</FactRow>
        <FactRow label="Tries so far">{x.tries}</FactRow>
      </Facts>
      <div className="flex flex-col gap-1.5">
        <b className="text-[14px]">Done by the system{x.openedAt ? `, ${boardWhen(x.openedAt)}` : ''}</b>
        <Done>
          Try {p.attemptNo} marked {p.status === 'REVERSED' ? 'returned' : 'failed'}
          {p.idempotencyKey ? `, matched by our reference ${p.idempotencyKey}` : ''}
        </Done>
        <Done>
          <Money paise={p.amountPaise} className="font-sans" /> {p.status === 'REVERSED' ? 'owed again' : 'still owed'} in his account book
        </Done>
        <Done>
          SMS in Hindi to his own phone <FarmerPhone id={p.farmerId} />
        </Done>
        {account && <Done>Automatic payments to {maskAccount(p.accountLast4).replace(' ', '')} stopped</Done>}
      </div>
      <div className="flex-1" />
      <Note>
        {account
          ? 'The reason is about his account: fix his bank details first, then pay again to the new account.'
          : 'The reason is not about his account: pay again to the same account.'}
      </Note>
      <PanelActions>
        {p.farmerId && (
          <LinkButton to={`/c/farmers/${p.farmerId}/bank?payout=${p.id}`} primary={account} icon={<Bank aria-hidden="true" />}>
            Fix bank details
          </LinkButton>
        )}
        <LinkButton to={`/c/payouts/${p.id}/pay-again`} primary={!account} icon={<ArrowClockwise aria-hidden="true" />}>
          Pay again
        </LinkButton>
      </PanelActions>
    </SidePanel>
  )
}
